import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';

import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, Button, Container, Stack, TextField, Typography } from '@mui/material';

import { DEFAULT_TAG_CONTENT_FORM_DATA, type TagContentFormData } from '~/components/Tags/TagContent/TagContentForm';
import TagContentUpdate from '~/components/Tags/TagContent/TagContentUpdate';

import isProd from '~/utils/isProd';

export default function ContentSearchPage() {
  const history = useHistory();
  const location = useLocation();
  const isProduction = isProd();
  const [tagNameInput, setTagNameInput] = useState('');
  const [searchedTagName, setSearchedTagName] = useState('');

  const { control, reset } = useForm<TagContentFormData>({
    defaultValues: DEFAULT_TAG_CONTENT_FORM_DATA,
    mode: 'onChange',
  });

  const queryTagName = useMemo(() => {
    return new URLSearchParams(location.search).get('query')?.trim() ?? '';
  }, [location.search]);

  const trimmedTagNameInput = useMemo(() => tagNameInput.trim(), [tagNameInput]);

  const runSearch = useCallback(
    (tagName: string) => {
      setTagNameInput(tagName);
      setSearchedTagName(tagName);
      reset(DEFAULT_TAG_CONTENT_FORM_DATA, { keepDirty: false });
    },
    [reset],
  );

  useEffect(() => {
    runSearch(queryTagName);
  }, [queryTagName, runSearch]);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!trimmedTagNameInput) {
      history.push('/marketing/content');
      return;
    }

    if (trimmedTagNameInput === queryTagName) {
      runSearch(trimmedTagNameInput);
      return;
    }

    history.push(`/marketing/content?query=${encodeURIComponent(trimmedTagNameInput)}`);
  };

  const handleResetContent = (data: TagContentFormData) => {
    reset(data, { keepDirty: false });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Content
      </Typography>

      <Box component="form" onSubmit={handleSearch}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
          <TextField
            label="Search for content by tag"
            value={tagNameInput}
            onChange={(event) => setTagNameInput(event.target.value)}
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<SearchIcon />}
            disabled={!trimmedTagNameInput}
            sx={{ minWidth: 140 }}
          >
            Search
          </Button>
        </Stack>
      </Box>

      {!isProduction && (
        <Box mt={3}>
          <Alert severity="warning">Content can only be created on production.</Alert>
        </Box>
      )}

      {isProduction && (
        <Box mt={4}>
          {searchedTagName ? (
            <TagContentUpdate
              heading={`Content for: ${searchedTagName}`}
              tagName={searchedTagName}
              control={control}
              onReset={handleResetContent}
            />
          ) : (
            <Alert severity="info">Search for a tag to view and manage its content.</Alert>
          )}
        </Box>
      )}
    </Container>
  );
}
